import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FiSearch, FiChevronDown, FiSettings, FiUser, FiLogOut, FiShield, FiHelpCircle, FiMenu, FiX } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useNavItems } from '../hooks/useNavItems';

const Header = () => {
  const { user } = useAuth();
  const [showProfile, setShowProfile] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const profileRef = useRef(null);

  // Logout is handled by the parent RiskWebApp — no separate logout needed
  const handleLogout = async () => {
    // Inform the user that logout is managed through the main portal
    console.log('[TrustCenter] Logout managed by Risk Assessment portal');
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfile(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const navItems = useNavItems([
    { to: '/',              label: 'Dashboard',     feature: 'cud_dashboard' },
    { to: '/requests',      label: 'Requests' },
    { to: '/organizations', label: 'Organizations' },
    { to: '/users',         label: 'Users',         roles: ['admin', 'assessmentadmin', 'department_admin', 'departmentadmin'] },
    { to: '/assessments',   label: 'Assessments', matchPaths: ['/assessments', '/questionnaires', '/questions'] },
    { to: '/documents',     label: 'Documents' },
  ]);

  const isNavActive = (item) => {
    if (item.matchPaths) {
      return item.matchPaths.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));
    }
    return location.pathname === item.to;
  };

  return (
    <header className={`bg-white border-slate-200 border-b sticky top-0 z-40`}>
      {/* Top bar with logo and user controls */}
      <div className={`flex items-center justify-between px-6 h-16`}>
        {/* Logo and Brand */}
        <Link to="/" className="flex items-center gap-3">
        
          <div className="hidden md:block">
            <h1 className={`text-2xl font-bold text-[#0077b5]`}>Trust Center</h1>
         
          </div>
        </Link>

        {/* Desktop Navigation - Hidden on Mobile */}
        <nav className={`hidden md:flex items-center justify-end gap-1`}>
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
                isNavActive(item)
                  ? 'border-[#ff6600] text-[#ff6600]'
                  : 'border-transparent text-slate-600 hover:text-slate-800'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setShowMobileMenu(!showMobileMenu)}
          className={`md:hidden p-2 rounded-lg transition-colors hover:bg-slate-100 text-slate-600`}
          aria-label="Toggle menu"
        >
          {showMobileMenu ? <FiX className="text-lg" /> : <FiMenu className="text-lg" />}
        </button>
      </div>

      {/* Mobile Navigation Menu */}
      {showMobileMenu && (
        <nav className={`md:hidden border-t border-slate-200 bg-slate-50/50`}>
          <div className="px-4 py-3 space-y-1 max-h-96 overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`block px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isNavActive(item)
                    ? 'bg-blue-50 text-[#ff6600]'
                    : 'text-slate-600 hover:text-slate-800 hover:bg-slate-100'
                }`}
                onClick={() => setShowMobileMenu(false)}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </nav>
      )}

    </header>
  );
};

export default Header;
